import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Clock, Building2, User, BarChart3, CheckCircle, Settings, X } from 'lucide-react';
import { Article, articles, getArticlesByCategory } from '../generated/articles';
import ArticleFeedback from './ArticleFeedback';

// Helper to get category display info
const getCategoryInfo = (category: Article['category']) => {
  switch (category) {
    case 'leaders':
      return {
        label: 'For Leaders',
        icon: Building2,
        bgColor: 'bg-teal-100',
        textColor: 'text-teal-700',
        iconColor: 'text-teal-600'
      };
    case 'employees':
      return {
        label: 'For Employees',
        icon: User,
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        iconColor: 'text-blue-600'
      };
    case 'insights':
      return {
        label: 'Assessment Insights',
        icon: BarChart3,
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-700',
        iconColor: 'text-purple-600'
      };
    case 'practice':
      return {
        label: 'In Practice',
        icon: CheckCircle,
        bgColor: 'bg-green-100',
        textColor: 'text-green-700',
        iconColor: 'text-green-600'
      };
    case 'tools':
      return {
        label: 'Tools & Resources',
        icon: Settings,
        bgColor: 'bg-orange-100',
        textColor: 'text-orange-700',
        iconColor: 'text-orange-600'
      };
  }
};

interface ArticleDetailV2Props {
  articleId: string;
  onNavigate?: (page: string) => void;
  onNavigateToArticle: (articleId: string) => void;
  onNavigateToResourcesList: () => void;
}

const ArticleDetailV2: React.FC<ArticleDetailV2Props> = ({
  articleId,
  onNavigate,
  onNavigateToArticle,
  onNavigateToResourcesList
}) => {
  const article = articles.find(a => a.id === articleId);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const contentRef = useRef<HTMLElement>(null);

  // Make images clickable for lightbox
  useEffect(() => {
    if (!contentRef.current) return;

    const images = contentRef.current.querySelectorAll('img');
    images.forEach((img) => {
      img.style.cursor = 'pointer';
      img.title = 'Click to enlarge';
      img.onclick = () => setLightboxImage(img.src);
    });
  }, [article]);

  // Close lightbox on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxImage(null);
    };
    if (lightboxImage) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [lightboxImage]);

  if (!article) {
    return (
      <div className="pt-16 pb-20 bg-gray-50 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8">The article you're looking for doesn't exist.</p>
          <button
            onClick={onNavigateToResourcesList}
            className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Resources
          </button>
        </div>
      </div>
    );
  }

  const categoryInfo = getCategoryInfo(article.category);
  const CategoryIcon = categoryInfo.icon;

  // Find related articles from the same category (excluding current)
  const relatedArticles = getArticlesByCategory(article.category)
    .filter(a => a.id !== article.id)
    .slice(0, 3);

  return (
    <div className="pt-16 pb-20 bg-gray-50 min-h-screen">
      {/* Article Header */}
      <section className="py-12 bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back link */}
          <button
            onClick={onNavigateToResourcesList}
            className="inline-flex items-center text-teal-300 hover:text-white mb-6 transition"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Resources
          </button>

          {/* Category badge */}
          <div className="flex items-center mb-4">
            <div className={`w-8 h-8 ${categoryInfo.bgColor} rounded-lg flex items-center justify-center mr-3`}>
              <CategoryIcon className={`w-4 h-4 ${categoryInfo.iconColor}`} />
            </div>
            <span className={`text-sm font-medium ${categoryInfo.bgColor} ${categoryInfo.textColor} px-3 py-1 rounded-full`}>
              {categoryInfo.label}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            {article.title}
          </h1>

          {/* Subtitle */}
          <p className="text-xl text-teal-100 mb-6">
            {article.subtitle}
          </p>

          {/* Meta */}
          <div className="flex items-center text-teal-200 text-sm">
            <Clock className="w-4 h-4 mr-2" />
            <span>{article.readTime}</span>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <style>{`
            .article-content p { margin-bottom: 1.25rem; line-height: 1.75; color: #374151; }
            .article-content h2 { margin-top: 2rem; margin-bottom: 1rem; font-size: 1.5rem; font-weight: 700; color: #111827; }
            .article-content h3 { margin-top: 1.5rem; margin-bottom: 0.75rem; font-size: 1.25rem; font-weight: 600; color: #111827; }
            .article-content ul, .article-content ol { margin: 1rem 0; padding-left: 1.5rem; }
            .article-content li { margin: 0.5rem 0; line-height: 1.75; color: #374151; }
            .article-content ul { list-style-type: disc; }
            .article-content ol { list-style-type: decimal; }
            .article-content a { color: #0d9488; }
            .article-content a:hover { text-decoration: underline; }
            .article-content strong { font-weight: 600; color: #111827; }
            .article-content blockquote { border-left: 4px solid #0d9488; padding-left: 1rem; margin: 1.5rem 0; font-style: italic; color: #4b5563; }
          `}</style>
          {(() => {
            // Split content at Sources section to insert feedback before it
            const sourcesMatch = article.content.match(/(<h3[^>]*>.*?Sources.*?<\/h3>)/i);
            if (sourcesMatch) {
              const splitIndex = article.content.indexOf(sourcesMatch[0]);
              const mainContent = article.content.substring(0, splitIndex);
              const sourcesContent = article.content.substring(splitIndex);
              return (
                <>
                  <article
                    ref={contentRef}
                    className="article-content text-lg"
                    dangerouslySetInnerHTML={{ __html: mainContent }}
                  />
                  {/* Feedback Widget - before Sources */}
                  <div className="mt-12 pt-8 border-t border-gray-200">
                    <ArticleFeedback articleId={article.id} articleTitle={article.title} />
                  </div>
                  {/* Sources section */}
                  <div
                    className="article-content text-lg mt-12"
                    dangerouslySetInnerHTML={{ __html: sourcesContent }}
                  />
                </>
              );
            }
            // Fallback if no Sources section found
            return (
              <>
                <article
                  ref={contentRef}
                  className="article-content text-lg"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
                {/* Feedback Widget */}
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <ArticleFeedback articleId={article.id} articleTitle={article.title} />
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Articles</h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {relatedArticles.map(related => (
                <div
                  key={related.id}
                  onClick={() => onNavigateToArticle(related.id)}
                  className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 cursor-pointer hover:shadow-xl transition"
                >
                  <h3 className="font-bold text-gray-900 mb-2 line-clamp-2">{related.title}</h3>
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">{related.subtitle}</p>
                  <span className="text-sm text-gray-500">{related.readTime}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
            Ready to Understand Your Team's AI Readiness?
          </h2>
          <p className="text-lg text-teal-100 mb-8 max-w-2xl mx-auto">
            The I AM Assessment reveals who's ready to lead, who needs support, and how to move your entire organization forward together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => onNavigate?.('pricing')}
              className="bg-teal-500 text-white px-8 py-4 rounded-full font-semibold hover:bg-teal-400 transition transform hover:-translate-y-0.5"
            >
              Explore Assessment Options
            </button>
            <button
              onClick={onNavigateToResourcesList}
              className="bg-white/10 text-white px-8 py-4 rounded-full font-semibold border border-white/30 hover:bg-white/20 transition"
            >
              More Resources
            </button>
          </div>
        </div>
      </section>

      {/* Image Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition"
            aria-label="Close lightbox"
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={lightboxImage}
            alt="Enlarged view"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default ArticleDetailV2;
